<template>
  <div>
    <v-dialog
      persistent
      max-width="480"
      v-model="dialog"
      overlay-opacity=".95"
    >
      <v-card rounded="lg">
        <v-card-title class="justify-space-between align-start">
          <div>
            <!-- Title and Helper message -->
            <h3 class="text-h6 font-weight-medium">Account Lookup</h3>
            <p class="text-subtitle-2 text--secondary mt-1 mb-0 text-wrap">
              Search using either one of the Unique Identifiers OR Account Holder details.
            </p>

            <!-- Clear Filters Button -->
            <v-btn
              depressed
              :disabled="!isAnyUniqueFieldSet && !areAccountDetailsFieldsSet"
              class="float-right subtitle-1 mt text-capitalize"
              @click="searchDetails = {...defaultSearchDetails}"
            >
              <v-icon class="subtitle-1">mdi-close</v-icon>
              Clear filters
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text>
          <v-alert
            dense
            type="warning"
            v-if="!valid"
            class="mt-1"
          >
            Check to ensure your search fields are <strong>valid</strong>.
          </v-alert>
          <v-card
            class="px-4 mt-3"
            outlined
          >

            <ValidationObserver ref="accountSearchObserver">
              <v-expansion-panels
                flat
                multiple
                mandatory
                v-model="activeExpansionPanel"
              >
                <!-- Unique identifiers search fields -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="px-0">
                    <span class="font-weight-medium grey-text text--darken-3 text-decoration-underline">UNIQUE IDENTIFIERS</span>
                  </v-expansion-panel-header>
                  
                  <v-expansion-panel-content class="px-0">
                    <v-row dense>
                      <v-col cols="12">
                        <ValidationProvider
                          name="Account Number"
                          :rules="'max:45'"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            :autofocus="true"
                            dense
                            outlined
                            label="Account Number"
                            :error-messages="errors"
                            :disabled="isTaxNumberFieldSet || areAccountDetailsFieldsSet"
                            v-model="searchDetails.mainAccountNumber"
                          ></v-text-field>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="12">
                        <ValidationProvider
                          name="Tax Registration Number"
                          :rules="'max:15'"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            dense
                            outlined
                            :error-messages="errors"
                            label="Tax Registration Number"
                            :disabled="isAccountNumberFieldSet || areAccountDetailsFieldsSet"
                            v-model="searchDetails.trn"
                          ></v-text-field>
                        </ValidationProvider>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Personal details search fields -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="px-0">

                    <span class="font-weight-medium grey--text text--darken-3 text-decoration-underline">ACCOUNT HOLDER DETAILS</span>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content class="px-0">
                    <v-row dense>
                      <v-col
                        cols="12"
                        md="6"
                      >
                        <ValidationProvider
                          name="First Name"
                          :rules="`${isAnyUniqueFieldSet ? '' : 'required|'}max:45`"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            dense
                            outlined
                            :label="`First Name ${isAnyUniqueFieldSet? '' : '*'}`"
                            :error-messages="errors"
                            :disabled="isAnyUniqueFieldSet"
                            v-model="searchDetails.firstName"
                          ></v-text-field>
                        </ValidationProvider>
                      </v-col>
                      <v-col
                        cols="12"
                        md="6"
                      >
                        <ValidationProvider
                          name="Last Name"
                          :rules="`${isAnyUniqueFieldSet ? '' : 'required|'}max:45`"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            dense
                            outlined
                            :error-messages="errors"
                            :label="`Last Name ${isAnyUniqueFieldSet? '' : '*'}`"
                            :disabled="isAnyUniqueFieldSet"
                            v-model="searchDetails.lastName"
                          ></v-text-field>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="12">
                        <ValidationProvider
                          name="Date Of Birth"
                          :rules="`${isAnyUniqueFieldSet ? '' : 'required|date|dob:15|'}max:45`"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            dense
                            outlined
                            type="date"
                            :label="`Date Of Birth ${isAnyUniqueFieldSet? '' : '*'}`"
                            :error-messages="errors"
                            :disabled="isAnyUniqueFieldSet"
                            v-model="searchDetails.dateOfBirth"
                          ></v-text-field>
                        </ValidationProvider>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </ValidationObserver>
          </v-card>
        </v-card-text>

        <!-- Modal Actions -->
        <v-card-actions class="pt-3 justify-end">
          <v-btn
            @click="closeModal()"
            min-width="120"
            min-height="42"
            depressed
            class="text-capitalize"
          >Cancel</v-btn>
          <v-btn
            @click="onSubmit()"
            min-width="120"
            min-height="42"
            depressed
            color="primary"
            class="text-capitalize"
          >Search</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style>
.v-expansion-panel-content__wrap {
  padding: 0px;
}
</style>

<script>
import PageMixin from '@/mixins/page.mixin';
export default {
  mixins: [PageMixin],
  data() {
    return {
      dialog: false,
      defaultSearchDetails: {
        mainAccountNumber: "",
        trn: "",
        firstName: "",
        lastName: "",
        dateOfBirth: "",
      },
      searchDetails: {},
      activeExpansionPanel: [0],
      valid: true,
    }
  },

  computed: {
    isTaxNumberFieldSet: function () {
      return !!this.searchDetails.trn?.trim();
    },
    isAccountNumberFieldSet: function () {
      return !!this.searchDetails.mainAccountNumber?.trim();
    },
    isAnyUniqueFieldSet: function() {
      const { mainAccountNumber, trn } = this.searchDetails;
      return !!mainAccountNumber?.trim() || !!trn?.trim();
    },
    areAccountDetailsFieldsSet: function() {
      const { firstName, lastName, dateOfBirth } = this.searchDetails;
      return !!firstName?.trim() || !!lastName?.trim() || !!dateOfBirth?.trim();
    }
  },
  
  methods: {
    async closeModal() {
      this.dialog = false;
      this.$refs.accountSearchObserver?.reset();
    },
    
    openModal(searchParams = this.defaultSearchDetails) {
      this.valid = true;
      this.dialog = true;
      this.searchDetails = {...searchParams}; 
      
      //check which expansion to open based on the 
      //populated information in the search details object.
      const { firstName } = this.searchDetails;
      this.activeExpansionPanel = [0];
      if(firstName) this.activeExpansionPanel = [1];
    },
    
    async onSubmit() {
      try {
        this.valid = await this.$refs.accountSearchObserver?.validate();
        const isEmpty = !Object.keys(this.searchDetails).some(k => this.searchDetails[k]);
        if (!this.valid || isEmpty) {
          this.valid = false;
          this.activeExpansionPanel = [0,1];
          return;
        }
        
        this.$emit('searchDetails', this.searchDetails);
        this.closeModal();
      } catch (error) {
        console.log(error);
      }
    }
  }
};
</script>
